import wretch from 'wretch';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useIsAuthenticated } from './user-info.hooks';
import { useRedirectToLogin } from './useRedirectToLogin';
import { useApiBase, useApiHeaders } from '../query';

export function useReusableContents(strTitle: string) {
  const params: ReusableContentRequest = {
    title: strTitle,
  };
  return useApiQuery<ReusableContents>('/cms/v1/reusable-contents', params);
}
export type ReusableContentsList = {
  comments: string;
  content: string;
  documentModifiedWhen: string;
  title: string;
};
export type ReusableContents = {
  reusableContents: ReusableContentsList[];
};
export type ReusableContentRequest = {
  title: string;
};

type ObjectMap = {
  [key: string]: any;
};

type ExtraHeaders = Partial<{
  [headerName: string]: string;
}>;

export function useApiQuery<TO = ObjectMap>(
  route: string,
  params?: ObjectMap,
  useQueryOptions?: UseQueryOptions<TO>,
  extraHeaders?: ExtraHeaders
) {
  const isAuthenticated = useIsAuthenticated();
  const apiGet = useApiGet<TO>();

  // Wait for authorization
  const enabled =
    isAuthenticated &&
    (useQueryOptions?.enabled || useQueryOptions?.enabled === undefined);

  return useQuery<TO>({
    enabled: enabled,
    queryFn: () => apiGet(route, params, extraHeaders),
    queryKey: [route, params, extraHeaders],
    retry: 0,
  });
}

const apiWretcher = wretch(undefined, { cache: 'no-store' }).options({
  credentials: 'same-origin',
  mode: 'cors',
});

function useApiGet<TO = ObjectMap>() {
  const getBaseUrl = useApiBase();
  const getApiHeaders = useApiHeaders();
  const redirectToLogin = useRedirectToLogin();

  return function (
    route: string,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const url = `${getBaseUrl(route)}${route}`;

    let apiCall = apiWretcher.url(url);
    if (params) {
      apiCall = apiCall.query(params);
    }
    return apiCall
      .headers(getApiHeaders(extraHeaders))
      .get()
      .unauthorized((err) => {
        redirectToLogin();
        throw err;
      })
      .json<TO>()
      .catch((error) => {
        console.error(error);
        throw error;
      });
  };
}
